import { COLLECTIONS } from '../../constants/collections';
import { CONFIG_CATEGORIES } from '../../constants/systemConfig';
import { createDocument, getDocument, getAllDocuments, updateDocument, deleteDocument } from './firestoreService';

/**
 * System Configuration Service - Sistem yapılandırmaları
 */

// ============ VEHICLE TYPE CONFIGS ============

// Create vehicle type configuration
export const createVehicleTypeConfig = async (configData) => {
    const data = {
        ...configData,
        category: CONFIG_CATEGORIES.VEHICLE_TYPES,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
    };
    return await createDocument(COLLECTIONS.SYSTEM_CONFIGS, data);
};

// Get all vehicle types for a tenant
export const getVehicleTypes = async (tenantId, activeOnly = true) => {
    // Simplified query to avoid index requirement
    const conditions = [
        { type: 'where', field: 'tenantId', operator: '==', value: tenantId },
        { type: 'where', field: 'category', operator: '==', value: CONFIG_CATEGORIES.VEHICLE_TYPES }
    ];

    let results = await getAllDocuments(COLLECTIONS.SYSTEM_CONFIGS, conditions);

    if (activeOnly) {
        results = results.filter(item => item.isActive !== false);
    }

    // Sort client side
    return results.sort((a, b) => a.name.localeCompare(b.name));
};

// Get vehicle type by code
export const getVehicleTypeByCode = async (tenantId, code) => {
    const conditions = [
        { type: 'where', field: 'tenantId', operator: '==', value: tenantId },
        { type: 'where', field: 'category', operator: '==', value: CONFIG_CATEGORIES.VEHICLE_TYPES },
        { type: 'where', field: 'code', operator: '==', value: code }
    ];

    const results = await getAllDocuments(COLLECTIONS.SYSTEM_CONFIGS, conditions);
    return results.length > 0 ? results[0] : null;
};

// Update vehicle type
export const updateVehicleTypeConfig = async (configId, updates) => {
    const data = {
        ...updates,
        updatedAt: new Date()
    };
    return await updateDocument(COLLECTIONS.SYSTEM_CONFIGS, configId, data);
};

// Delete vehicle type
export const deleteVehicleTypeConfig = async (configId) => {
    return await deleteDocument(COLLECTIONS.SYSTEM_CONFIGS, configId);
};

// ============ LOADING RULE CONFIGS ============

// Create loading rule configuration
export const createLoadingRuleConfig = async (ruleData) => {
    const data = {
        ...ruleData,
        category: CONFIG_CATEGORIES.LOADING_RULES,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
    };
    return await createDocument(COLLECTIONS.SYSTEM_CONFIGS, data);
};

// Get all loading rules for a tenant
export const getLoadingRules = async (tenantId, activeOnly = true) => {
    // Simplified query to avoid index requirement
    const conditions = [
        { type: 'where', field: 'tenantId', operator: '==', value: tenantId },
        { type: 'where', field: 'category', operator: '==', value: CONFIG_CATEGORIES.LOADING_RULES }
    ];

    let results = await getAllDocuments(COLLECTIONS.SYSTEM_CONFIGS, conditions);

    if (activeOnly) {
        results = results.filter(item => item.isActive !== false);
    }

    return results;
};

// Get loading rule by vehicle type code
export const getLoadingRuleByVehicleType = async (tenantId, vehicleTypeCode) => {
    const conditions = [
        { type: 'where', field: 'tenantId', operator: '==', value: tenantId },
        { type: 'where', field: 'category', operator: '==', value: CONFIG_CATEGORIES.LOADING_RULES },
        { type: 'where', field: 'vehicleTypeCode', operator: '==', value: vehicleTypeCode }
    ];

    const results = await getAllDocuments(COLLECTIONS.SYSTEM_CONFIGS, conditions);
    return results.length > 0 ? results[0] : null;
};

// Update loading rule
export const updateLoadingRuleConfig = async (configId, updates) => {
    const data = {
        ...updates,
        updatedAt: new Date()
    };
    return await updateDocument(COLLECTIONS.SYSTEM_CONFIGS, configId, data);
};

// Delete loading rule
export const deleteLoadingRuleConfig = async (configId) => {
    return await deleteDocument(COLLECTIONS.SYSTEM_CONFIGS, configId);
};

// ============ DOCK TYPE CONFIGS ============

// Create dock type configuration
export const createDockTypeConfig = async (configData) => {
    const data = {
        ...configData,
        category: CONFIG_CATEGORIES.DOCK_TYPES,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
    };
    return await createDocument(COLLECTIONS.SYSTEM_CONFIGS, data);
};

// Get all dock types for a tenant
export const getDockTypes = async (tenantId, activeOnly = true) => {
    // Simplified query
    const conditions = [
        { type: 'where', field: 'tenantId', operator: '==', value: tenantId },
        { type: 'where', field: 'category', operator: '==', value: CONFIG_CATEGORIES.DOCK_TYPES }
    ];

    let results = await getAllDocuments(COLLECTIONS.SYSTEM_CONFIGS, conditions);

    if (activeOnly) {
        results = results.filter(item => item.isActive !== false);
    }

    // Sort client side
    return results.sort((a, b) => a.name.localeCompare(b.name));
};

// Update dock type
export const updateDockTypeConfig = async (configId, updates) => {
    const data = {
        ...updates,
        updatedAt: new Date()
    };
    return await updateDocument(COLLECTIONS.SYSTEM_CONFIGS, configId, data);
};

// Delete dock type
export const deleteDockTypeConfig = async (configId) => {
    return await deleteDocument(COLLECTIONS.SYSTEM_CONFIGS, configId);
};

// ============ TRANSPORTER TYPE CONFIGS ============

// Create transporter type configuration
export const createTransporterTypeConfig = async (configData) => {
    const data = {
        ...configData,
        category: CONFIG_CATEGORIES.TRANSPORTER_TYPES,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
    };
    return await createDocument(COLLECTIONS.SYSTEM_CONFIGS, data);
};

// Get all transporter types for a tenant
export const getTransporterTypes = async (tenantId, activeOnly = true) => {
    // Simplified query
    const conditions = [
        { type: 'where', field: 'tenantId', operator: '==', value: tenantId },
        { type: 'where', field: 'category', operator: '==', value: CONFIG_CATEGORIES.TRANSPORTER_TYPES }
    ];

    let results = await getAllDocuments(COLLECTIONS.SYSTEM_CONFIGS, conditions);

    if (activeOnly) {
        results = results.filter(item => item.isActive !== false);
    }

    // Sort client side
    return results.sort((a, b) => a.name.localeCompare(b.name));
};

// Delete transporter type
export const deleteTransporterTypeConfig = async (configId) => {
    return await deleteDocument(COLLECTIONS.SYSTEM_CONFIGS, configId);
};

// ============ GENERAL CONFIG OPERATIONS ============

// Get any config by ID
export const getSystemConfig = async (configId) => {
    return await getDocument(COLLECTIONS.SYSTEM_CONFIGS, configId);
};

// Get all configs by category
export const getConfigsByCategory = async (tenantId, category) => {
    const conditions = [
        { type: 'where', field: 'tenantId', operator: '==', value: tenantId },
        { type: 'where', field: 'category', operator: '==', value: category }
    ];

    return await getAllDocuments(COLLECTIONS.SYSTEM_CONFIGS, conditions);
};

// Get general settings specifically
export const getGeneralSettings = async (tenantId) => {
    const conditions = [
        { type: 'where', field: 'tenantId', operator: '==', value: tenantId },
        { type: 'where', field: 'category', operator: '==', value: CONFIG_CATEGORIES.GENERAL_SETTINGS }
    ];
    const results = await getAllDocuments(COLLECTIONS.SYSTEM_CONFIGS, conditions);

    // Sort by updatedAt desc to get the latest config if duplicates exist
    results.sort((a, b) => {
        const dateA = a.updatedAt?.toDate ? a.updatedAt.toDate() : new Date(a.updatedAt || 0);
        const dateB = b.updatedAt?.toDate ? b.updatedAt.toDate() : new Date(b.updatedAt || 0);
        return dateB - dateA; // Descending
    });

    return results.length > 0 ? results[0] : null;
};
